/**
 * Tooltip implementation for Utility Connect
 */

const rootSelector = '[data-utility-connect-tooltip-root]';
const toggleSelector = '[data-utility-connect-tooltip-toggle]';
const panelSelector = '[data-utility-connect-tooltip-panel]';
const mobileGutter = 16;
const desktopGutter = 24;
const tooltipGap = 12;
const arrowPadding = 32;
const desktopBreakpoint = 768;

let hasInitialized = false;

/**
 * Updates the position of the tooltip panel relative to its toggle
 * @param {HTMLElement} root 
 */
const updateTooltipPosition = (root) => {
  const toggle = root.querySelector(toggleSelector);
  const panel = root.querySelector(panelSelector);

  if (!toggle || !panel || panel.classList.contains('tw-hidden')) {
    return;
  }

  const rootRect = root.getBoundingClientRect();
  const toggleRect = toggle.getBoundingClientRect();
  const horizontalGutter =
    window.innerWidth >= desktopBreakpoint ? desktopGutter : mobileGutter;
  const availableWidth = Math.max(window.innerWidth - horizontalGutter * 2, 0);
  const targetWidth =
    window.innerWidth >= desktopBreakpoint
      ? Math.min(400, availableWidth)
      : availableWidth;

  panel.style.width = `${targetWidth}px`;
  panel.style.maxWidth =
    window.innerWidth >= desktopBreakpoint ? '400px' : 'none';
  panel.style.removeProperty('left');
  panel.style.removeProperty('right');

  const panelRect = panel.getBoundingClientRect();
  const toggleCenter = toggleRect.left + toggleRect.width / 2;
  const maxLeft = Math.max(
    horizontalGutter,
    window.innerWidth - panelRect.width - horizontalGutter
  );
  const panelLeft = Math.min(
    Math.max(toggleCenter - panelRect.width / 2, horizontalGutter),
    maxLeft
  );
  const panelTop =
    toggleRect.top - rootRect.top - panelRect.height - tooltipGap;

  panel.style.left = `${panelLeft - rootRect.left}px`;
  panel.style.top = `${panelTop}px`;

  const arrowLeft = toggleCenter - panelLeft;
  const clampedArrowLeft = Math.min(
    Math.max(arrowLeft, arrowPadding),
    panelRect.width - arrowPadding
  );

  panel.style.setProperty(
    '--utility-connect-tooltip-arrow-left',
    `${clampedArrowLeft}px`
  );
};

const closeTooltip = (root) => {
  const toggle = root.querySelector(toggleSelector);
  const panel = root.querySelector(panelSelector);

  if (panel) {
    panel.classList.add('tw-hidden');
  }

  if (toggle) {
    toggle.setAttribute('aria-expanded', 'false');
  }
};

const closeAllTooltips = () => {
  document.querySelectorAll(rootSelector).forEach(closeTooltip);
};

const bindUtilityConnectTooltips = () => {
  if (hasInitialized) {
    return;
  }

  hasInitialized = true;

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest(toggleSelector);

    if (toggle) {
      const root = toggle.closest(rootSelector);
      const panel = root ? root.querySelector(panelSelector) : null;
      const isOpening = Boolean(panel?.classList.contains('tw-hidden'));

      closeAllTooltips();

      if (root && panel && isOpening) {
        panel.classList.remove('tw-hidden');
        toggle.setAttribute('aria-expanded', 'true');
        requestAnimationFrame(() => {
          updateTooltipPosition(root);
        });
      }

      return;
    }

    if (!event.target.closest(rootSelector)) {
      closeAllTooltips();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeAllTooltips();
    }
  });

  window.addEventListener('resize', () => {
    document.querySelectorAll(rootSelector).forEach(updateTooltipPosition);
  });
};

export const initUtilityConnectTooltip = () => {
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      bindUtilityConnectTooltips,
      {
        once: true,
      }
    );
    return;
  }

  bindUtilityConnectTooltips();
};
